import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Table,
    Image,
    Badge,
    Modal,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import axios from "axios";

const BannerPage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);

    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        title: "",
        short_detail: "",
        is_main: false,
        img: null,
    });

    const [preview, setPreview] = useState(null);

    const getAuthHeaders = () => {
        const token = localStorage.getItem("authToken");
        return {
            Authorization: token ? `Bearer ${token}` : "",
        };
    };

    const getBanners = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/banners`,
                { headers: getAuthHeaders() }
            );

            setBanners(response.data);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load banners.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getBanners();
    }, []);

    const getImageUrl = (img) => {
        if (!img) return "";
        if (img.startsWith("http://") || img.startsWith("https://")) return img;
        return `${process.env.REACT_APP_IMG_URL}/${img}`;
    };

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === "file") {
            const file = files?.[0] || null;
            setForm((prev) => ({ ...prev, img: file }));

            if (file) {
                setPreview(URL.createObjectURL(file));
            } else {
                setPreview(null);
            }

            setErrors((prev) => ({ ...prev, img: "" }));
            return;
        }

        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        const title = form.title.trim();
        const shortDetail = form.short_detail.trim();

        if (!title) newErrors.title = "Title is required.";
        if (!shortDetail) newErrors.short_detail = "Short detail is required.";
        if (!editingBanner && !form.img) newErrors.img = "Banner image is required.";

        if (form.img) {
            const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
            if (!allowedTypes.includes(form.img.type)) {
                newErrors.img = "Only JPG, JPEG, PNG, and WEBP images are allowed.";
            }
            if (form.img.size > 5 * 1024 * 1024) {
                newErrors.img = "Image size must not exceed 5MB.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setForm({ title: "", short_detail: "", is_main: false, img: null });
        setPreview(null);
        setEditingBanner(null);
        setErrors({});
        setError("");
    };

    const openAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const openEditModal = (banner) => {
        setEditingBanner(banner);
        setForm({
            title: banner.title || "",
            short_detail: banner.short_detail || "",
            is_main: Boolean(banner.is_main),
            img: null,
        });
        setPreview(getImageUrl(banner.img));
        setErrors({});
        setError("");
        setShowModal(true);
    };

    const closeModal = () => {
        if (!saving) {
            setShowModal(false);
            resetForm();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setError("");

        if (!validateForm()) return;

        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            formData.append("title", form.title.trim());
            formData.append("short_detail", form.short_detail.trim());
            formData.append("is_main", form.is_main ? "1" : "0");

            if (form.img) {
                formData.append("img", form.img);
            }

            if (editingBanner) {
                formData.append("_method", "PUT");

                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/banners/${editingBanner.id}`,
                    formData,
                    { headers: getAuthHeaders() }
                );

                setMessage("Banner updated successfully.");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/banners`,
                    formData,
                    { headers: getAuthHeaders() }
                );

                setMessage("Banner added successfully.");
            }

            setShowModal(false);
            resetForm();
            await getBanners();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
                setError(err.response.data.message || "Please fix validation errors.");
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteBanner = async (id) => {
        if (!window.confirm("Are you sure you want to delete this banner?")) return;

        try {
            setDeleting(id);
            setError("");
            setMessage("");

            await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/banners/${id}`,
                { headers: getAuthHeaders() }
            );

            setMessage("Banner deleted successfully.");
            await getBanners();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete banner.");
        } finally {
            setDeleting(null);
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Banner Management</h2>
                        <p className="text-muted mb-0">Manage website banners.</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" onClick={openAddModal}>
                            + Add Banner
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && !showModal && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-3 text-muted">Loading banners...</div>
                            </div>
                        ) : banners.length === 0 ? (
                            <div className="text-center py-5">
                                <h5>No banners found.</h5>
                                <Button variant="primary" className="mt-2" onClick={openAddModal}>
                                    + Add Banner
                                </Button>
                            </div>
                        ) : (
                            <Table responsive hover className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th style={{ width: "180px" }}>Image</th>
                                        <th>Title</th>
                                        <th>Short Detail</th>
                                        <th>Main</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {banners.map((banner, index) => (
                                        <tr key={banner.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <Image
                                                    src={getImageUrl(banner.img)}
                                                    width={160}
                                                    height={90}
                                                    rounded
                                                    alt={banner.title}
                                                    style={{ objectFit: "cover" }}
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = "none";
                                                    }}
                                                />
                                            </td>
                                            <td><strong>{banner.title}</strong></td>
                                            <td>
                                                <div style={{ maxWidth: "350px" }} className="text-muted">
                                                    {banner.short_detail}
                                                </div>
                                            </td>
                                            <td>
                                                {Boolean(banner.is_main) ? (
                                                    <Badge bg="success">Main</Badge>
                                                ) : (
                                                    <Badge bg="secondary">Normal</Badge>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    className="me-2"
                                                    onClick={() => openEditModal(banner)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="danger"
                                                    disabled={deleting === banner.id}
                                                    onClick={() => deleteBanner(banner.id)}
                                                >
                                                    {deleting === banner.id ? "Deleting..." : "Delete"}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                <Modal show={showModal} onHide={closeModal} centered size="lg" backdrop="static">
                    <Form onSubmit={handleSubmit}>
                        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
                            <Modal.Title className="fw-bold fs-4">
                                {editingBanner ? "Edit Banner" : "Add Banner"}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="p-4">
                            {error && <Alert variant="danger">{error}</Alert>}
                            <Row className="g-4 align-items-start">
                                <Col md={6}>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                            Banner Image
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            name="img"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleChange}
                                            isInvalid={!!errors.img}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.img) ? errors.img[0] : errors.img}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {preview && (
                                        <div className="mt-3">
                                            <Image
                                                src={preview}
                                                rounded
                                                className="w-100 shadow-sm"
                                                style={{ height: "180px", objectFit: "cover" }}
                                            />
                                        </div>
                                    )}
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                            Title
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={form.title}
                                            onChange={handleChange}
                                            isInvalid={!!errors.title}
                                            className="bg-light border-0 py-2 px-3"
                                            placeholder="Enter banner title"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-bold text-uppercase text-muted small mb-2">
                                            Short Detail
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            name="short_detail"
                                            value={form.short_detail}
                                            onChange={handleChange}
                                            isInvalid={!!errors.short_detail}
                                            className="bg-light border-0 py-2 px-3"
                                            placeholder="Enter short details"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {Array.isArray(errors.short_detail) ? errors.short_detail[0] : errors.short_detail}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="pt-2">
                                        <Form.Check
                                            type="switch"
                                            id="is_main"
                                            name="is_main"
                                            label="Set as main banner"
                                            checked={form.is_main}
                                            onChange={handleChange}
                                            className="fw-semibold text-secondary"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>

                        <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center">
                            <Button
                                variant="light"
                                onClick={closeModal}
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold bg-light text-secondary border-0"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={saving}
                                className="px-4 py-2 text-uppercase fw-semibold"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editingBanner ? (
                                    "Update Banner"
                                ) : (
                                    "Add Banner"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default BannerPage;